import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import ReusableSelect from './ReusableSelect';
import EditableTable from '../settings/components/EditableTable';
import {
  deleteFirmwareVersion as deleteFirmwareVersionApi,
  fetchFirmwareData,
} from './nodesHelper';
import './nodeConfig.less';

type FirmwareRow = {
  id: number;
  deviceType?: string;
  swVersion?: string;
  status?: string;
  [key: string]: any;
};

const deviceTypeOptions = [
  { value: 'field_node', label: 'Field Node' },
  { value: 'control_node', label: 'Control Node' },
  { value: 'energy_node', label: 'Energy Node' },
];

const firmwareStatusOptions = ['show all', 'release', 'alpha', 'beta', 'engineering'];

const columnLabels: Record<string, string> = {
  id: 'ID',
  presignedUrl: 'Presigned URL',
  deviceType: 'Device Type',
  swVersion: 'Software Version',
  fileSize: 'File Size',
  fileHash: 'File Hash',
  minSwRequired: 'Minimum Software Required',
  minSwAfterUpgrade: 'Minimum Software After Upgrade',
  status: 'Status',
  createdAt: 'Created At',
  updatedAt: 'Updated At',
  uploadedBy: 'Uploaded By',
};

const visibleColumnKeys = ['id', 'swVersion', 'status'];

const DeleteFirmwareVersion: React.FC = () => {
  const { t } = useTranslation();
  const [deviceType, setDeviceType] = useState('field_node');
  const [firmwareStatus, setFirmwareStatus] = useState<string | undefined>(undefined);
  const [firmwareRows, setFirmwareRows] = useState<FirmwareRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadFirmwareData = useCallback(async (selectedType: string) => {
    setLoading(true);
    try {
      const response = await fetchFirmwareData(selectedType);
      const rows = (response || []).map((row: FirmwareRow) => ({
        ...row,
        key: row.id,
      }));
      setFirmwareRows(rows);
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: t('Failed to load firmware versions'),
      });
      setFirmwareRows([]);
      setSelectedRowKeys([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadFirmwareData(deviceType);
  }, [deviceType, loadFirmwareData]);

  const tableColumns = useMemo(
    () => visibleColumnKeys.map((key) => ({
      title: columnLabels[key] || key,
      dataIndex: key,
      key,
      render: (value: any) => value ?? '-',
    })),
    [],
  );

  const filteredFirmwareRows = useMemo(
    () => (
      firmwareStatus && firmwareStatus !== 'show all'
        ? firmwareRows.filter((row) => row.status === firmwareStatus)
        : firmwareRows
    ),
    [firmwareRows, firmwareStatus],
  );

  const selectedFirmwareRow = useMemo(
    () => firmwareRows.find((row) => row.id === selectedRowKeys[0]),
    [firmwareRows, selectedRowKeys],
  );

  const handleDelete = async () => {
    if (!selectedFirmwareRow) {
      notification.warning({
        message: t('Please select a firmware version'),
      });
      return;
    }

    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedFirmwareRow) {
      return;
    }

    setDeleting(true);
    try {
      await deleteFirmwareVersionApi({
        firmware_id: selectedFirmwareRow.id,
      });
      await loadFirmwareData(deviceType);
      notification.success({
        message: t('Firmware versions deleted'),
      });
      setShowDeleteModal(false);
    } catch (error) {
      notification.error({
        message: t('Failed to delete firmware versions'),
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="single-column-page padded desktop-container-config">
      <div className="margin-bottom-10">
        <h1 className="font-bold text-align-center delete-firmware-title">
          {t('Delete Firmware Version')}
        </h1>
      </div>

      <div
        className="margin-bottom-10 delete-firmware-filter-row"
        data-testid="filter-row"
      >
        <div className="delete-firmware-filter-item">
          <span className="font-weight-bold delete-firmware-filter-label">
            {t('Device Type')}
            :
          </span>
          <ReusableSelect
            options={deviceTypeOptions}
            value={deviceType}
            defaultValue="field_node"
            onChange={setDeviceType}
            className="fixed-width-100"
          />
        </div>

        <div className="delete-firmware-filter-item">
          <span className="font-weight-bold delete-firmware-filter-label">
            {t('Firmware Status')}
            :
          </span>
          <ReusableSelect
            options={firmwareStatusOptions}
            value={firmwareStatus}
            onChange={setFirmwareStatus}
            placeholder={t('please select')}
            className="fixed-width-100"
          />
        </div>
      </div>

      <EditableTable
        columns={tableColumns}
        data={filteredFirmwareRows}
        showIsActive={false}
        showAction={false}
        loading={loading}
        pageSize={10}
        isCheckable
        selectionType="radio"
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={(keys) => setSelectedRowKeys(keys.slice(0, 1))}
        rowKeyField="id"
      />

      <div className="flex-row justify-content-end margin-top-10">
        <Button
          danger
          type="primary"
          loading={deleting}
          disabled={!selectedFirmwareRow}
          onClick={handleDelete}
        >
          {t('Delete Selected')}
        </Button>
      </div>
      <Modal
        title={t('Confirm')}
        centered
        open={showDeleteModal}
        onOk={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        okText={t('Delete')}
        cancelText={t('Cancel')}
        okType="danger"
        confirmLoading={deleting}
        cancelButtonProps={{ disabled: deleting }}
      >
        <p>{t('Are you sure you want to delete?')}</p>
      </Modal>
    </div>
  );
};

export default DeleteFirmwareVersion;
